import React from "react";
import { formatEur, parseDurationDiscounts } from "../utils";
import { useQuote } from "../context/QuoteContext";
import { Boxes } from "lucide-react";

export const PricePerM3Card = ({ total, volume, note, showBoxCount = false }) => {
  const { settings } = useQuote();
  const perM3 = volume > 0 ? total / volume : 0;
  const BOX_M3 = 0.6 * 0.4 * 0.4; // scatola standard 60x40x40 cm = 0.096 m³
  const boxCount = volume > 0 ? Math.round(volume / BOX_M3) : 0;
  const discs = parseDurationDiscounts(settings?.durationDiscounts).filter((d) => Number(d.months) > 0 && Number(d.percent) > 0);
  const rows = [
    { label: "Base", total, perM3, cls: "text-[#1a5f5a]" },
    ...discs.map((d, i) => {
      const f = 1 - Number(d.percent) / 100;
      return { label: `${Number(d.months)} mesi −${Number(d.percent)}%`, total: total * f, perM3: perM3 * f, cls: i % 2 === 0 ? "text-emerald-600" : "text-emerald-700" };
    }),
  ];
  const discLabel = discs.map((d) => `${Number(d.months)} mesi −${Number(d.percent)}%`).join(", ");
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="price-per-m3-card">
      <div className="flex items-center gap-3 mb-4">
        <Boxes className="h-5 w-5 text-[#1a5f5a]" />
        <h3 className="font-display font-semibold text-lg">Prezzo per m³</h3>
      </div>
      <div className={`grid ${showBoxCount ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"} gap-3 text-center`}>
        <div className="rounded-md bg-slate-50 border border-slate-100 p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Volume</div>
          <div className="text-lg font-display font-semibold text-slate-800 mt-1" data-testid="ppm3-volume">{(volume || 0).toFixed(2)} m³</div>
        </div>
        {showBoxCount && (
          <div className="rounded-md bg-amber-50 border border-amber-100 p-3" data-testid="ppm3-boxes-tile">
            <div className="text-[10px] uppercase tracking-[0.15em] text-amber-600">Scatole 60×40×40</div>
            <div className="text-lg font-display font-semibold text-amber-700 mt-1" data-testid="ppm3-boxes">≈ {boxCount}<span className="text-xs font-normal text-slate-400"> scatole</span></div>
          </div>
        )}
        <div className="rounded-md bg-slate-50 border border-slate-100 p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Totale mensile</div>
          <div className="text-lg font-display font-semibold text-slate-800 mt-1" data-testid="ppm3-total">€{formatEur(total)}</div>
        </div>
        <div className="rounded-md bg-[#1a5f5a]/10 border border-[#1a5f5a]/15 p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#134744]">Prezzo / m³</div>
          <div className="text-lg font-display font-semibold text-[#1a5f5a] mt-1" data-testid="ppm3-value">€{formatEur(perM3)}<span className="text-xs font-normal text-slate-400">/m³</span></div>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left p-2.5 font-medium">Sconto</th>
              <th className="text-right p-2.5 font-medium">€/mese</th>
              <th className="text-right p-2.5 font-medium">€/m³</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.label} data-testid={`ppm3-row-${r.label}`}>
                <td className={`p-2.5 font-medium ${r.cls}`}>{r.label}</td>
                <td className="p-2.5 text-right text-slate-700">€{formatEur(r.total)}<span className="text-slate-400 text-xs">/mese</span></td>
                <td className="p-2.5 text-right text-slate-700">€{formatEur(r.perM3)}<span className="text-slate-400 text-xs">/m³</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-400 mt-3">
        {note || `Calcolato sui prezzi (inclusi quelli impostati manualmente) e sui volumi degli oggetti indicati.${discLabel ? ` Sconti: ${discLabel}.` : ""}`}
      </p>
    </div>
  );
};
